import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { RefreshCw, Sparkles, TrendingUp, ArrowRight } from 'lucide-react';
import { useTodayQuote, useRandomQuote } from '@/hooks/useQuote';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { QuoteActions } from '@/components/quote/QuoteActions';
import { QuoteSkeleton } from '@/components/ui/Skeleton';
import { downloadQuoteAsImage } from '@/utils/download';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 },
};

const stagger = {
  animate: { transition: { staggerChildren: 0.1 } },
};

export function Home() {
  const { data: todayData, isLoading: todayLoading } = useTodayQuote();
  const { data: randomData, isLoading: randomLoading, refetch: refetchRandom, isFetching } = useRandomQuote();
  const quoteCardRef = useRef<HTMLDivElement>(null);

  const { data: categoriesData } = useQuery<{ success: boolean; data: any[] }>({
    queryKey: ['categories'],
    queryFn: () => api.categories.getAll(),
  });

  const { data: authorsData } = useQuery<{ success: boolean; data: any[] }>({
    queryKey: ['authors'],
    queryFn: () => api.authors.getAll(),
  });

  const { data: trendingData } = useQuery<{ success: boolean; data: any[] }>({
    queryKey: ['quotes', 'trending'],
    queryFn: () => api.quotes.getAll({ sort: 'popular', limit: '6' }),
  });

  const todayQuote = todayData?.data;
  const randomQuote = randomData?.data;
  const categories = categoriesData?.data || [];
  const authors = authorsData?.data || [];
  const trendingQuotes = trendingData?.data || [];

  const handleDownload = async () => {
    if (quoteCardRef.current) {
      await downloadQuoteAsImage(quoteCardRef.current, `quote-${Date.now()}`, 'png');
    }
  };

  return (
    <motion.div initial="initial" animate="animate" variants={stagger}>
      {/* Hero Section */}
      <section className="max-w-4xl mx-auto px-4 pt-16 pb-24 text-center">
        <motion.div variants={fadeUp}>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-50 text-primary-600 text-sm font-medium mb-8">
            <Sparkles size={16} />
            Daily Inspiration
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-6">
            <span className="gradient-text">Quote of the Day</span>
          </h1>
          <p className="text-xl text-surface-500 max-w-2xl mx-auto mb-12">
            Beautiful quotes for daily inspiration, curated from the world's greatest minds.
          </p>
        </motion.div>

        {/* Today's Quote */}
        <motion.div variants={fadeUp} className="mb-12">
          {todayLoading ? (
            <QuoteSkeleton />
          ) : todayQuote ? (
            <div ref={quoteCardRef} className="glass-card p-8 md:p-12 text-left relative overflow-hidden">
              <div className="absolute top-4 right-4 text-6xl font-serif text-primary-200/40 select-none">"</div>
              <p className="text-2xl md:text-3xl leading-relaxed mb-8 relative z-10">
                {todayQuote.text}
              </p>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  {todayQuote.author_name && (
                    <Link
                      to={`/author/${todayQuote.author_slug}`}
                      className="text-lg font-semibold hover:text-primary-500 transition-colors"
                    >
                      {todayQuote.author_name}
                    </Link>
                  )}
                  {todayQuote.category_name && (
                    <Link
                      to={`/category/${todayQuote.category_slug}`}
                      className="block text-sm text-surface-500 hover:text-primary-500 transition-colors"
                    >
                      {todayQuote.category_name}
                    </Link>
                  )}
                </div>
                <QuoteActions
                  quoteId={todayQuote.id}
                  text={todayQuote.text}
                  author={todayQuote.author_name}
                  onDownload={handleDownload}
                />
              </div>
            </div>
          ) : (
            <div className="glass-card p-12 text-center">
              <p className="text-xl text-surface-500">No quote available today</p>
            </div>
          )}
        </motion.div>

        {/* Random Quote Button */}
        <motion.div variants={fadeUp}>
          <button
            onClick={() => refetchRandom()}
            disabled={isFetching}
            className="btn-secondary"
          >
            <RefreshCw size={18} className={isFetching ? 'animate-spin' : ''} />
            Get Random Quote
          </button>
        </motion.div>

        {/* Random Quote Result */}
        {randomQuote && !randomLoading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 glass-card p-6 text-left"
          >
            <p className="text-lg leading-relaxed mb-4">"{randomQuote.text}"</p>
            <p className="text-sm text-surface-500">— {randomQuote.author_name || 'Unknown'}</p>
          </motion.div>
        )}
      </section>

      {/* Popular Categories */}
      {categories.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 pb-16">
          <motion.div variants={fadeUp}>
            <h2 className="text-2xl font-bold mb-6">Popular Categories</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {categories.slice(0, 8).map((cat: any) => (
                <Link key={cat.slug} to={`/category/${cat.slug}`}>
                  <motion.div
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    className="glass-card p-4 text-center cursor-pointer"
                  >
                    <p className="font-semibold">{cat.name}</p>
                    <p className="text-sm text-surface-500">{cat.quote_count || 0} quotes</p>
                  </motion.div>
                </Link>
              ))}
            </div>
          </motion.div>
        </section>
      )}

      {/* Featured Authors */}
      {authors.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 pb-16">
          <motion.div variants={fadeUp}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Featured Authors</h2>
              <Link to="/authors" className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors">
                View all <ArrowRight size={16} />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {authors.slice(0, 8).map((author: any) => (
                <Link key={author.slug} to={`/author/${author.slug}`}>
                  <motion.div
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    className="glass-card p-4 flex items-center gap-3 cursor-pointer"
                  >
                    <div className="w-12 h-12 rounded-full gradient-bg flex items-center justify-center text-white font-bold text-lg shrink-0">
                      {author.name?.charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{author.name}</p>
                      <p className="text-sm text-surface-500">{author.quote_count || 0} quotes</p>
                    </div>
                  </motion.div>
                </Link>
              ))}
            </div>
          </motion.div>
        </section>
      )}

      {/* Trending Quotes */}
      {trendingQuotes.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 pb-24">
          <motion.div variants={fadeUp}>
            <div className="flex items-center gap-2 mb-6">
              <TrendingUp size={24} className="text-primary-500" />
              <h2 className="text-2xl font-bold">Trending Quotes</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {trendingQuotes.slice(0, 6).map((q: any) => (
                <Link key={q.id} to={`/quote/${q.id}`}>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="glass-card p-5 cursor-pointer"
                  >
                    <p className="text-base leading-relaxed mb-3">"{q.text}"</p>
                    <p className="text-sm text-surface-500">— {q.author_name || 'Unknown'}</p>
                  </motion.div>
                </Link>
              ))}
            </div>
          </motion.div>
        </section>
      )}

      {/* Hidden download target */}
      <div className="hidden">
        <div ref={quoteCardRef} className="p-8 bg-white" />
      </div>
    </motion.div>
  );
}
